/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { useEffect, useState } from "react"
import { useSkeem } from "../providers/SkeemProvider"
import { List, Grid, Spacer, Placeholder } from "@cd2/components"
import { H2, P, TextBlock } from "@cd2/text"
import { IRecord } from "../types/schema"
import { Card } from "./Card"
import { CGButton } from "./CGButton"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useMediaBreakpoints } from "../hooks/useMediaBreakpoints"
import { ToggleSwitch } from "./ToggleSwitch"
import { ContactUsButton } from "./ContactUsButton"
import { LoadingFrame } from "../pages/user/UserResultsShowPage/AssessmentDetailsTab"

type IPlan = IRecord<
  "plans",
  | "name"
  | "machineName"
  | "pricePennies"
  | "chargeFrequency"
  | "userLimit"
  | "canWhiteLabel"
  | "canHaveTeams"
  | "oneHourHrPricePennies"
> & { includesOneHourHR: boolean }

interface IPlanGroup {
  name: string
  plans: { yearly: string; monthly: string }
  userLimit: number
  coaching: boolean
  canWhiteLabel: boolean
  canHaveTeams: boolean
}

const planGroups: IPlanGroup[] = [
  {
    name: "1 User",
    plans: { yearly: "1-yearly", monthly: "1-monthly" },
    userLimit: 1,
    coaching: true,
    canWhiteLabel: true,
    canHaveTeams: false,
  },
  {
    name: "5 Users",
    plans: { yearly: "5-yearly", monthly: "5-monthly" },
    userLimit: 2,
    coaching: true,
    canWhiteLabel: true,
    canHaveTeams: true,
  },
  {
    name: "10 Users",
    plans: { yearly: "10-yearly", monthly: "10-monthly" },
    userLimit: 10,
    coaching: true,
    canWhiteLabel: true,
    canHaveTeams: true,
  },
  {
    name: "25 Users",
    plans: { yearly: "25-yearly", monthly: "25-monthly" },
    userLimit: 25,
    coaching: true,
    canWhiteLabel: true,
    canHaveTeams: true,
  },
  {
    name: "50 Users",
    plans: { yearly: "50-yearly", monthly: "50-monthly" },
    userLimit: 50,
    coaching: true,
    canWhiteLabel: true,
    canHaveTeams: true,
  },
  {
    name: "100 Users",
    plans: { yearly: "100-yearly", monthly: "100-monthly" },
    userLimit: 100,
    coaching: true,
    canWhiteLabel: true,
    canHaveTeams: true,
  },
  {
    name: "250 Users",
    plans: { yearly: "250-yearly", monthly: "250-monthly" },
    userLimit: 250,
    coaching: true,
    canWhiteLabel: true,
    canHaveTeams: true,
  },
]

export function PlanSelector({
  onSelectPlan,
  thin,
  currentPlanDetails,
}: {
  onSelectPlan(plan: IPlan & { includesOneHourHR: boolean }): void
  thin?: boolean
  currentPlanDetails?: { userLimit?: number; price?: number }
}) {
  const { isMobile, isTablet } = useMediaBreakpoints()
  const skeem = useSkeem()
  const [plans, setPlans] = useState<IPlan[] | null>(null)
  const [chargeYearly, setChargeYearly] = useState(true)
  const [includesOneHourHR, setIncludesOneHourHR] = useState(false)

  useAsyncEffect(async () => {
    const machineNames: string[] = []
    for (const planGroup of planGroups) {
      if (planGroup.plans) {
        machineNames.push(planGroup.plans.yearly, planGroup.plans.monthly)
      }
    }
    const plans = await skeem.fetch<IPlan>({
      plans: {
        attributes: [
          "name",
          "machineName",
          "pricePennies",
          "chargeFrequency",
          "userLimit",
          "canWhiteLabel",
          "canHaveTeams",
          "oneHourHrPricePennies",
        ],
        filter: { matches: { machineName: machineNames, archived: false } },
      },
    })
    setPlans(plans)
  }, [])

  const columns = isMobile ? 1 : isTablet ? (thin ? 1 : 2) : thin ? 2 : 4

  return (
    <div>
      <Grid columns={["auto", "max-content"]} alignItems="end" gap={16}>
        <div
          css={css`
            width: max-content;
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            display: grid;
            grid-template-columns: max-content auto;
            grid-gap: 8px;
            align-items: center;
          `}
        >
          <ToggleSwitch
            on={includesOneHourHR}
            onClick={() => setIncludesOneHourHR(!includesOneHourHR)}
          />
          <div>
            <b>Include One Hour HR</b>
            <br />
            <i>Include one hour per month of HR support</i>
          </div>
        </div>
        <List justifyContent="center" alignItems="center" horizontal>
          <ToggleSwitch on={chargeYearly} onClick={() => setChargeYearly(!chargeYearly)} />
          <b>Annual billing</b>
        </List>
      </Grid>
      <Spacer />
      <Grid columns={{ repeat: { times: columns, size: { fr: 1 } } }} gap={16}>
        {planGroups.map(planGroup => (
          <PlanGroupCard
            key={planGroup.name}
            planGroup={planGroup}
            plans={plans}
            onSelectPlan={plan => onSelectPlan({ ...plan, includesOneHourHR })}
            chargeFrequency={chargeYearly ? "yearly" : "monthly"}
            currentPlanDetails={currentPlanDetails}
            includesOneHourHR={includesOneHourHR}
          />
        ))}
        <Card css={css("display: flex; flex-direction: column; border: 1px solid #ddd")} border>
          <div>
            <H2 color="primary">Enterprise</H2>
            <div css={css("height: 110px")}>
              <P>
                <span css={css("font-size: 1.5em")}>Contact for pricing</span>
              </P>
            </div>
            <List>
              <div>
                <b>250+</b> users
              </div>
              <div>
                Access to <b>all</b> coaching resources
              </div>
              <div>
                Personalise your portal with our <b>white labelling</b> tools
              </div>
              <div>
                Create and manage <b>teams</b>
              </div>
              <div>Bespoke integration</div>
            </List>
            <Spacer size={24} />
            <div>
              <ContactUsButton>Contact us for pricing</ContactUsButton>
            </div>
          </div>
          <div css={css("flex: 1")} />
        </Card>
      </Grid>
    </div>
  )
}

function PlanGroupCard({
  planGroup,
  plans,
  onSelectPlan,
  chargeFrequency,
  currentPlanDetails,
  includesOneHourHR,
}: {
  planGroup: IPlanGroup
  plans: IPlan[] | null
  onSelectPlan(plan: IPlan): void
  chargeFrequency?: "yearly" | "monthly"
  currentPlanDetails?: { userLimit?: number; price?: number }
  includesOneHourHR?: boolean
}) {
  const yearlyPlan = plans?.find(plan => plan.machineName === planGroup.plans.yearly) ?? null
  const monthlyPlan = plans?.find(plan => plan.machineName === planGroup.plans.monthly) ?? null
  const currentPlan = chargeFrequency === "monthly" ? monthlyPlan : yearlyPlan

  const divisor = chargeFrequency === "monthly" ? 1 : 12
  let pricePennies = currentPlan?.pricePennies ? currentPlan.pricePennies / divisor : null

  if (pricePennies !== null && includesOneHourHR) {
    pricePennies += currentPlan?.oneHourHrPricePennies ?? 0
  }

  return (
    <Card css={css("display: flex; flex-direction: column; border: 1px solid #ddd")} border>
      <div>
        <H2 color="primary">{planGroup.name}</H2>
        <div css={css("height: 90px")}>
          <TextBlock blockSpacing={0}>
            <P>
              <span css={css("font-size: 1.5em")}>
                £{pricePennies === null ? "-" : (pricePennies / 100).toFixed(2)}
              </span>
              <span>/mo</span> ex VAT
            </P>
            <P color="#aaa">
              billed {chargeFrequency === "monthly" ? "monthly" : "annually"}
              <br />
              {""}minimum 1 year term
            </P>
          </TextBlock>
        </div>
        <List>
          {!!currentPlan?.userLimit && (
            <div>
              <b>{planGroup.userLimit}</b> {planGroup.userLimit === 1 ? "user" : "users"}
            </div>
          )}
          {!!planGroup.coaching && (
            <div>
              Access to <b>all</b> coaching resources
            </div>
          )}
          {!!currentPlan?.canWhiteLabel && (
            <div>
              Personalise your portal with our <b>white labelling</b> tools
            </div>
          )}
          {!!currentPlan?.canHaveTeams && (
            <div>
              Create and manage <b>teams</b>
            </div>
          )}
          {!!includesOneHourHR && (
            <div>
              Include <b>1 hour</b> of HR support per month.
            </div>
          )}
        </List>
        <Spacer size={24} />
      </div>
      <div css={css("flex: 1")} />
      {!plans ? (
        <CGButton>
          <Placeholder width="100px" height={8} />
        </CGButton>
      ) : currentPlan ? (
        <CGButton
          onClick={() => onSelectPlan(currentPlan)}
          disabled={
            currentPlanDetails && (planGroup.userLimit ?? 0) <= (currentPlanDetails?.userLimit ?? 0)
          }
        >
          Choose this plan
        </CGButton>
      ) : (
        <div />
      )}
    </Card>
  )
}

type NewPlan = IRecord<"perUserPlans", "name" | "minUsers" | "maxUsers" | "pricePenniesPerUser"> & {
  includesOneHourHR: boolean
}

export function NewPlanSelector({ onSelectPlan }: { onSelectPlan(plan: NewPlan): void }) {
  const skeem = useSkeem()
  const [plans, setPlans] = useState<NewPlan[] | null>(null)
  const [includesOneHourHR, setIncludesOneHourHR] = useState(false)

  useEffect(() => {
    try {
      const signupData = JSON.parse(localStorage.getItem("signup-plan") || "{}")

      if (signupData && "includesOneHourHR" in signupData && "plan" in signupData) {
        setIncludesOneHourHR(signupData.includesOneHourHR)
        onSelectPlan({ ...signupData.plan, includesOneHourHR: signupData.includesOneHourHR })
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useAsyncEffect(async () => {
    const plans = await skeem.fetch<NewPlan>({
      perUserPlans: {
        attributes: ["name", "minUsers", "maxUsers", "pricePenniesPerUser"],
      },
    })
    setPlans(plans)
  }, [])

  if (!plans) {
    return <LoadingFrame />
  }

  const maxUsers = Math.max(...plans.map(plan => plan.maxUsers))

  return (
    <div>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: end;
          margin-bottom: 12px;
        `}
      >
        <div
          css={css`
            width: max-content;
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            display: grid;
            grid-template-columns: max-content auto;
            grid-gap: 8px;
            align-items: center;
          `}
        >
          <ToggleSwitch
            on={includesOneHourHR}
            onClick={() => setIncludesOneHourHR(!includesOneHourHR)}
          />
          <div>
            <b>Include One Hour HR</b>
            <br />
            <i>Include one hour per month of HR support</i>
          </div>
        </div>
      </div>
      <Grid
        columns={{ repeat: { times: "auto-fit", size: { min: 200, max: { fr: 1 } } } }}
        gap={16}
      >
        {plans.map(plan => (
          <Card key={plan.id}>
            <div
              css={css`
                text-align: center;
              `}
            >
              <Spacer size={16} />
              <div
                css={css`
                  font-size: 24px;
                  color: #666;
                `}
              >
                {plan.minUsers === plan.maxUsers
                  ? `${plan.minUsers} users`
                  : `${plan.minUsers}-${plan.maxUsers} users`}
              </div>
              <Spacer size={16} />
              <div
                css={css`
                  font-size: 28px;
                  color: #3b9097;
                  font-weight: bold;
                `}
              >
                £{(plan.pricePenniesPerUser / 100).toFixed(2)}
                <span
                  css={css`
                    font-size: 18px;
                    color: grey;
                    font-weight: normal;
                  `}
                >
                  + VAT
                </span>
              </div>
              <Spacer size={16} />
              <div
                css={css`
                  color: grey;
                `}
              >
                per user per month
              </div>
              <Spacer size={16} />
              {includesOneHourHR && (
                <div
                  css={css`
                    color: grey;
                  `}
                >
                  + £60 per month for 1 hour of HR support
                </div>
              )}
              <div
                css={css`
                  text-align: right;
                `}
              >
                <CGButton
                  onClick={() => {
                    onSelectPlan({ ...plan, includesOneHourHR })
                  }}
                >
                  Select
                </CGButton>
              </div>
            </div>
          </Card>
        ))}
        <Card>
          <div
            css={css`
              font-size: 30px;
              color: #666;
              display: flex;
              flex-direction: column;
              justify-content: center;
              height: 100%;
            `}
          >
            <div
              css={css`
                text-align: center;
              `}
            >
              {maxUsers}+ users
            </div>
            <Spacer size={16} />
            <P align="center">Contact for pricing</P>
          </div>
        </Card>
      </Grid>
    </div>
  )
}
